import React, { FC, Fragment, useContext, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation } from '@apollo/client';
import { machinesTemplate } from 'apollo/templates/organization/query.graphql';
import { deleteMachineTemplate } from 'apollo/templates/organization/mutation.graphql';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Machine } from 'models/organization';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import AddEditMachineDialog from './add-edit-machine-dialog/add-edit-machine-dialog.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 200 },
    { type: 'text', width: 120 },
    { type: 'text', width: 200 },
    { type: 'button', width: 80 },
];

const Machines: FC = () => {
    const { t: translate } = useTranslation('lab-settings');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedMachine, setSelectedMachine] = useState<Machine | undefined>();
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading } = useQuery<{ machines: Machine[] }>(machinesTemplate);
    const machines = data?.machines ?? [];

    const [deleteMachine] = useMutation(deleteMachineTemplate, {
        refetchQueries: [{ query: machinesTemplate }],
    });

    const handleAddClick = () => {
        setSelectedMachine(undefined);
        setDialogOpen(true);
    };

    const handleEditClick = (machine: Machine) => {
        setSelectedMachine(machine);
        setDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmId) return;
        try {
            await deleteMachine({ variables: { machineId: deleteConfirmId } });
            setDeleteConfirmId(null);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:MACHINE_DELETED_SUCCESSFULLY'),
            });
        } catch (error) {
            setDeleteConfirmId(null);
            showBoundary(error);
        }
    };

    const decimalToTimeString = (decimal: number): string => {
        const hours = Math.floor(decimal);
        const minutes = Math.round((decimal - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    return (
        <Fragment>
            <Box mb={1}>
                <Button startIcon={<AddIcon />} onClick={handleAddClick}>
                    {translate('lab-settings:ADD_MACHINE')}
                </Button>
            </Box>
            <Paper variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('lab-settings:MACHINE_NAME')}</TableCell>
                                <TableCell>{translate('lab-settings:DEFAULT_MACHINE_TIME')}</TableCell>
                                <TableCell>{translate('lab-settings:MACHINE_SCHEDULE')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />}
                            {machines.map((machine) => (
                                <TableRow
                                    key={machine._id}
                                    hover
                                    onClick={() => handleEditClick(machine)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{machine.name}</TableCell>
                                    <TableCell>
                                        {machine.defaultMachineTime !== undefined
                                            ? decimalToTimeString(machine.defaultMachineTime)
                                            : '—'}
                                    </TableCell>
                                    <TableCell>{machine.machineSchedule?.join(', ') || '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteConfirmId(machine._id ?? '');
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!machines.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        {translate('lab-settings:NO_MACHINES_FOUND')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <AddEditMachineDialog
                machine={selectedMachine}
                open={dialogOpen}
                onDialogClose={() => setDialogOpen(false)}
            />

            <ConfirmDialog
                open={!!deleteConfirmId}
                handleClose={() => setDeleteConfirmId(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('common:DELETE')}
                dialogContent={translate('lab-settings:CONFIRM_DELETE_MACHINE')}
                confirmBtnLabel={translate('common:DELETE')}
            />
        </Fragment>
    );
};

export default Machines;
